import { useCallback, useEffect, useRef, useState } from 'react'
import { type MicCheckResult, type MicCheckThresholds, clippedCount, evaluate, peak, rms } from './micCheckAnalysis'

export type MicCheckPhase = 'idle' | 'requesting' | 'recording' | 'analyzing' | 'result' | 'error'

export interface UseMicCheckOptions {
  durationSec?: number
  thresholds?: Partial<MicCheckThresholds>
}

export interface UseMicCheckReturn {
  phase: MicCheckPhase
  elapsedSec: number
  result: MicCheckResult | null
  error: string | null
  devices: MediaDeviceInfo[]
  selectedDeviceId: string | null
  recordedUrl: string | null
  recordedBlob: Blob | null
  analyser: AnalyserNode | null
  start: () => Promise<void>
  stop: () => void
  reset: () => void
  selectDevice: (deviceId: string) => Promise<void>
}

const ANALYZING_DELAY_MS = 600
const MIN_RECORD_SEC = 3
const SILENCE_STOP_SEC = 2
const SILENCE_RMS_ABS = 0.006
const VOICE_REL_FACTOR = 0.15

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus'
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm'
  if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4'
  return ''
}

export function useMicCheck({ durationSec = 12, thresholds }: UseMicCheckOptions = {}): UseMicCheckReturn {
  const [phase, setPhase] = useState<MicCheckPhase>('idle')
  const [elapsedSec, setElapsedSec] = useState(0)
  const [result, setResult] = useState<MicCheckResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null)
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null)
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const timeDataRef = useRef<Float32Array<ArrayBuffer> | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const rmsFramesRef = useRef<number[]>([])
  const peakFramesRef = useRef<number[]>([])
  const clippedRef = useRef(0)
  const lastVoiceAtRef = useRef(0)
  const voiceStartedRef = useRef(false)
  const maxRmsRef = useRef(0)
  const rafRef = useRef<number | null>(null)
  const startAtRef = useRef(0)
  const urlRef = useRef<string | null>(null)
  const stopRequestedRef = useRef(false)
  const unmountedRef = useRef(false)
  const runIdRef = useRef(0)

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect()
      } catch {
        /* noop */
      }
      sourceRef.current = null
    }
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect()
      } catch {
        /* noop */
      }
      analyserRef.current = null
    }
    setAnalyser(null)
  }, [])

  const populateDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices()
      if (unmountedRef.current) return
      setDevices(all.filter((d) => d.kind === 'audioinput'))
    } catch {
      /* noop */
    }
  }, [])

  const ensureAudio = useCallback(
    async (deviceId: string | null) => {
      releaseStream()

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1,
          sampleRate: 48000,
          ...(deviceId ? { deviceId: { exact: deviceId } } : {})
        }
      })
      streamRef.current = stream

      const actualId = stream.getAudioTracks()[0]?.getSettings().deviceId ?? deviceId ?? null
      setSelectedDeviceId(actualId)

      if (!audioCtxRef.current) {
        const Ctx =
          window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
        audioCtxRef.current = new Ctx()
      }
      if (audioCtxRef.current.state === 'suspended') await audioCtxRef.current.resume()

      const source = audioCtxRef.current.createMediaStreamSource(stream)
      const node = audioCtxRef.current.createAnalyser()
      node.fftSize = 2048
      node.smoothingTimeConstant = 0.3
      source.connect(node)
      sourceRef.current = source
      analyserRef.current = node
      timeDataRef.current = new Float32Array(node.fftSize)
      setAnalyser(node)

      await populateDevices()
    },
    [populateDevices, releaseStream]
  )

  const tick = useCallback(() => {
    const node = analyserRef.current
    const buf = timeDataRef.current
    if (!node || !buf) return

    node.getFloatTimeDomainData(buf)
    const level = rms(buf)
    rmsFramesRef.current.push(level)
    peakFramesRef.current.push(peak(buf))
    clippedRef.current += clippedCount(buf)

    const elapsed = (performance.now() - startAtRef.current) / 1000
    setElapsedSec(elapsed)

    const voiceThreshold = Math.max(SILENCE_RMS_ABS, maxRmsRef.current * VOICE_REL_FACTOR)
    if (level > voiceThreshold) {
      voiceStartedRef.current = true
      lastVoiceAtRef.current = elapsed
    }
    if (level > maxRmsRef.current) maxRmsRef.current = level

    const trailingSilence =
      voiceStartedRef.current && elapsed >= MIN_RECORD_SEC && elapsed - lastVoiceAtRef.current >= SILENCE_STOP_SEC

    if (elapsed >= durationSec || stopRequestedRef.current || trailingSilence) {
      if (recorderRef.current && recorderRef.current.state === 'recording') {
        recorderRef.current.stop()
      }
      rafRef.current = null
      return
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [durationSec])

  const finalizeRecording = useCallback(() => {
    const runId = runIdRef.current
    const isStale = () => unmountedRef.current || runId !== runIdRef.current

    setPhase('analyzing')

    const mime = recorderRef.current?.mimeType || 'audio/webm'
    const blob = new Blob(chunksRef.current, { type: mime })
    releaseStream()
    const url = URL.createObjectURL(blob)
    if (isStale()) {
      URL.revokeObjectURL(url)
      return
    }
    if (urlRef.current) URL.revokeObjectURL(urlRef.current)
    urlRef.current = url
    setRecordedUrl(url)
    setRecordedBlob(blob)

    const evaluation = evaluate(rmsFramesRef.current, peakFramesRef.current, clippedRef.current, [], thresholds)

    setTimeout(() => {
      if (isStale()) return
      setResult(evaluation)
      setPhase('result')
    }, ANALYZING_DELAY_MS)
  }, [releaseStream, thresholds])

  const start = useCallback(async () => {
    runIdRef.current++
    setError(null)
    setResult(null)
    stopRequestedRef.current = false
    rmsFramesRef.current = []
    peakFramesRef.current = []
    clippedRef.current = 0
    lastVoiceAtRef.current = 0
    voiceStartedRef.current = false
    maxRmsRef.current = 0
    chunksRef.current = []
    setElapsedSec(0)

    try {
      setPhase('requesting')
      await ensureAudio(selectedDeviceId)
      if (unmountedRef.current) return

      const mimeType = pickMimeType()
      const rec = mimeType ? new MediaRecorder(streamRef.current!, { mimeType }) : new MediaRecorder(streamRef.current!)
      recorderRef.current = rec
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
      }
      rec.onstop = () => {
        void finalizeRecording()
      }
      rec.start()

      startAtRef.current = performance.now()
      setPhase('recording')
      rafRef.current = requestAnimationFrame(tick)
    } catch (err) {
      const e = err as DOMException & { message?: string; name?: string }
      setError(
        e?.name === 'NotAllowedError'
          ? 'Microphone access denied — allow it in your browser settings and try again.'
          : `Could not access microphone: ${e?.message || e?.name || 'unknown error'}`
      )
      setPhase('error')
    }
  }, [ensureAudio, finalizeRecording, selectedDeviceId, tick])

  const stop = useCallback(() => {
    stopRequestedRef.current = true
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop()
    }
  }, [])

  const reset = useCallback(() => {
    runIdRef.current++
    stopRequestedRef.current = true
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      try {
        recorderRef.current.stop()
      } catch {
        /* noop */
      }
    }
    recorderRef.current = null
    chunksRef.current = []
    rmsFramesRef.current = []
    peakFramesRef.current = []
    clippedRef.current = 0
    lastVoiceAtRef.current = 0
    voiceStartedRef.current = false
    maxRmsRef.current = 0
    releaseStream()
    setElapsedSec(0)
    setResult(null)
    setError(null)
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current)
      urlRef.current = null
    }
    setRecordedUrl(null)
    setRecordedBlob(null)
    setPhase('idle')
  }, [releaseStream])

  const selectDevice = useCallback(
    async (deviceId: string) => {
      setSelectedDeviceId(deviceId)
      reset()
      try {
        await ensureAudio(deviceId)
      } catch {
        /* user may deny; next start() will retry */
      }
    },
    [ensureAudio, reset]
  )

  useEffect(() => {
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      if (recorderRef.current && recorderRef.current.state === 'recording') {
        try {
          recorderRef.current.stop()
        } catch {
          /* noop */
        }
      }
      releaseStream()
      if (audioCtxRef.current) {
        void audioCtxRef.current.close().catch(() => undefined)
        audioCtxRef.current = null
      }
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current)
        urlRef.current = null
      }
    }
  }, [releaseStream])

  return {
    phase,
    elapsedSec,
    result,
    error,
    devices,
    selectedDeviceId,
    recordedUrl,
    recordedBlob,
    analyser,
    start,
    stop,
    reset,
    selectDevice
  }
}
